import { ENDPOINTS } from "@constants";
import AbstractService from "./AbstractService";

export default class PaymentService extends AbstractService {
	static endpoint = ENDPOINTS.payments;

	static verifyBulkPaymentStatus(JobId) {
		return this.get({
			endpoint: `${this.endpoint}/status/`,
			queryParams: { JobId },
		});
	}

	static makePayment(PaymentCardId, Amount, InvoiceIds) {
		return this.post({
			endpoint: this.endpoint,
			data: {
				PaymentCardId,
				Amount,
				InvoiceIds,
			},
		});
	}

	static save({ data, editMode }) {
		return this.request({
			endpoint: `${ENDPOINTS.accounts}/${this.endpoint}`,
			method: !editMode ? "POST" : "PUT",
			data,
		});
	}

	static getData() {
		return this.get({
			endpoint: `${ENDPOINTS.accounts}/${this.endpoint}`,
		});
	}

	static getPayouts({ pagingToken, displayCount = 25 }) {
		return this.get({
			endpoint: ENDPOINTS.payouts,
			queryParams: {
				pagingToken,
				displayCount,
			},
		});
	}

	static addPaymentMethod(data) {
		return this.post({
			endpoint: `${ENDPOINTS.accounts}/${ENDPOINTS.paymentMethods}`,
			data,
		});
	}

	static setPreferredCard(Id) {
		return this.post({
			endpoint: `${ENDPOINTS.accounts}/${ENDPOINTS.paymentMethods}/${Id}`,
		});
	}

	static deletePaymentMethod(Id) {
		return this.del({
			endpoint: `${ENDPOINTS.accounts}/${ENDPOINTS.paymentMethods}/${Id}`,
		});
	}

	static verifyBankAccount(data) {
		return this.post({
			endpoint: `${ENDPOINTS.accounts}/${this.endpoint}/verify`,
			data,
		});
	}

	static getNonRKPayInvoices(getCancelToken, queryParams) {
		return this.get({
			endpoint: ENDPOINTS.nonRKPayInvoices,
			queryParams,
			getCancelToken,
		});
	}

	static getNonRKPayInvoicesV4(getCancelToken, queryParams) {
		return this.get({
			endpoint: `${ENDPOINTS.nonRKPayInvoices}/v4`,
			queryParams,
			getCancelToken,
		});
	}

	static getNonRKPayInvoicesV4Counts(getCancelToken, queryParams) {
		return this.get({
			endpoint: `${ENDPOINTS.nonRKPayInvoices}/v4/counts`,
			queryParams,
			getCancelToken,
		});
	}

	static getNonRKPayInvoicePayments(invoiceId) {
		return this.get({
			endpoint: `NonRKPayInvoices/${invoiceId}/payments`,
		});
	}

	static getNonRKPayInvoiceRefundsInTransit(invoiceId) {
		return this.get({
			endpoint: `NonRKPayInvoices/${invoiceId}/refundsintransit`,
		});
	}

	static getNonRKPayInvoiceOfficers(invoiceId, data) {
		return this.get({
			endpoint: `NonRKPayInvoices/${invoiceId}/officers`,
			data,
		});
	}

	static createNonRKPayInvoicePayment(invoiceId, data) {
		return this.post({
			endpoint: `NonRKPayInvoices/${invoiceId}/payments`,
			data,
		});
	}

	static deleteNonRKPayInvoicePayments(invoiceId, data) {
		return this.put({
			endpoint: `NonRKPayInvoices/${invoiceId}/payments/unapply`,
			data,
		});
	}

	static getOrganizationPaymentMethods(organizationId) {
		return this.get({
			endpoint: `${ENDPOINTS.organizations}/${organizationId}/${ENDPOINTS.paymentMethods}`,
		});
	}

	static deleteOrganizationPaymentMethod(organizationId, paymentMethodId) {
		return this.del({
			endpoint: `${ENDPOINTS.organizations}/${organizationId}/${ENDPOINTS.paymentMethods}/${paymentMethodId}`,
		});
	}

	static saveOrganizationPaymentMethod(organizationId, data) {
		return this.post({
			endpoint: `${ENDPOINTS.organizations}/${organizationId}/${ENDPOINTS.paymentMethods}`,
			data,
		});
	}

	static verifyOrganizationBankAccount(data) {
		const { organizationId, ...rest } = data;
		return this.post({
			endpoint: `${ENDPOINTS.organizations}/${organizationId}/${ENDPOINTS.paymentMethods}/verification`,
			data: rest,
		});
	}

	static setOrganizationPaymentMethodStatus(organizationId, data) {
		return this.put({
			endpoint: `${ENDPOINTS.organizations}/${organizationId}/${ENDPOINTS.paymentMethods}`,
			data,
		});
	}

	static saveOrganizationDepositAccount(organizationId, data) {
		return this.post({
			endpoint: `${ENDPOINTS.organizations}/${organizationId}/depositaccounts`,
			data,
		});
	}

	static getOrganizationDepositAccounts(organizationId) {
		return this.get({
			endpoint: `${ENDPOINTS.organizations}/${organizationId}/depositaccounts`,
		});
	}

	static setOrganizationFeeToDepositAccount(organizationId, data) {
		return this.post({
			endpoint: `${ENDPOINTS.organizations}/${organizationId}/depositaccounts/setfee`,
			data,
		});
	}

	static updateOrganizationDepositAccount(organizationId, depositAccountId, data) {
		return this.put({
			endpoint: `${ENDPOINTS.organizations}/${organizationId}/depositaccounts/${depositAccountId}`,
			data,
		});
	}

	static getOrganizationBalance(organizationId) {
		return this.get({
			endpoint: `${ENDPOINTS.accounts}/${this.endpoint}/${organizationId}/balance`,
		});
	}

	static getBalanceHistory(organizationId) {
		return this.get({
			endpoint: `${ENDPOINTS.accounts}/${this.endpoint}/${organizationId}/balancetransactions`,
		});
	}

	static createRefund(data) {
		return this.post({
			endpoint: `${ENDPOINTS.accounts}/${this.endpoint}/refund`,
			data,
		});
	}

	static getChargeRefunds(chargeId) {
		return this.get({
			endpoint: `${ENDPOINTS.accounts}/charge/${chargeId}/refundedamount`,
		});
	}

	static validateNonRKPayInvoicesToSend(invoiceIds) {
		const invoiceToSend = invoiceIds.split(",");
		return this.get({
			endpoint: `${ENDPOINTS.nonRKPayInvoices}/validatesummary`,
			queryParams: { invoiceIds: invoiceToSend },
		});
	}

	static sendV4Invoices(data) {
		return this.post({
			endpoint: `${ENDPOINTS.nonRKPayInvoices}/sendV4invoices`,
			data,
		});
	}

	static sendBulkInvoices(data) {
		return this.post({
			endpoint: `${ENDPOINTS.nonRKPayInvoices}/sendbulkinvoices`,
			data,
		});
	}

	static getNonRKPayInvoicePrepayments(invoiceId) {
		return this.get({
			endpoint: `NonRKPayInvoices/${invoiceId}/prepayments`,
		});
	}
}
